import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, Sparkles } from "lucide-react";
import { Reveal } from "@/components/motion/Reveal";
import { BlogByline } from "@/components/BlogByline";
import { ReadWithLlm } from "@/components/ReadWithLlm";
import { buildBlogHead } from "@/lib/blogMeta";
import { RelatedReading } from "@/components/RelatedReading";
import { ClusterLinks } from "@/components/ClusterLinks";
import { AuthorityFlow } from "@/components/AuthorityFlow";
import { relatedArticles } from "@/lib/clusters";
import { ClinicalProductPitch } from "@/components/ClinicalProductPitch";
import heroAsset from "@/assets/blog/liver-health-supplements-hero.webp";

const SLUG = "liver-health-supplements-india";
const URL = `https://truwe.in/blog/${SLUG}`;
const TITLE =
  "Liver Health Supplements: What Actually Helps (India Guide) | TruWe";
const PUBLISHED = "2026-09-17";

const FAQS = [
  {
    q: "Do liver health supplements actually work?",
    a: "A well-formulated one can support the liver's own antioxidant and detoxification pathways — that is what the research on NAC and silymarin describes. What no supplement does is undo a diet, alcohol intake or weight pattern that is loading the liver in the first place. Nutrition and daily habits do the heavy lifting; the supplement supports them.",
  },
  {
    q: "Which ingredients matter most in a liver supplement?",
    a: "NAC, silymarin from milk thistle in a liposomal form, alpha-lipoic acid, curcumin in an absorbable form, and a named multi-strain probiotic for the gut side of the gut-liver axis. Each should be listed with its exact amount on the label.",
  },
  {
    q: "How long before a liver supplement makes a difference?",
    a: "Give it 8 to 12 weeks of daily use alongside dietary changes. The liver regenerates and adapts over weeks and months, not days, so anything promising a result in a week is selling a story rather than nutrition.",
  },
  {
    q: "Is a liver supplement safe to take every day?",
    a: "At standard supplemental doses, actives like NAC and silymarin have a long history of daily use and are generally well tolerated. Anyone on prescription medication, with gallbladder disease, or who is pregnant or breastfeeding should check with a doctor first.",
  },
  {
    q: "Are liver supplements useful for fatty liver?",
    a: "They are studied as support alongside the things that actually shift fatty liver — weight, sugar intake, alcohol and movement. Treat a supplement as one part of that plan and stay under a doctor's care for a diagnosed fatty liver.",
  },
  {
    q: "What is the difference between liver detox and liver support?",
    a: "Mostly marketing. Detox suggests a flush; support describes what a good formula does, which is supply the nutrition your liver's own pathways run on. Read the ingredient list, not the word on the front of the bottle.",
  },
  {
    q: "Should a liver supplement include a probiotic?",
    a: "There is a strong case for it. Around 70% of the blood reaching the liver arrives from the gut, so gut barrier health directly affects the load the liver carries. A named, enteric-protected multi-strain probiotic addresses that side.",
  },
  {
    q: "Can I get the same benefit from food alone?",
    a: "Food comes first — cruciferous vegetables, dals, fruit, coffee, less fried food and less alcohol all matter more than any capsule. A supplement fills the gaps that everyday nutrition rarely covers consistently, particularly the antioxidant actives at studied amounts.",
  },
];

export const Route = createFileRoute("/blog/liver-health-supplements-india")({
  component: Article,
  head: () =>
    buildBlogHead({
      slug: SLUG,
      title: TITLE,
      description:
        "Liver health supplements in India, explained plainly: the actives with real evidence, how to read a label, what nutrition and daily habits do first, and what no capsule can fix.",
      ogTitle: "Liver Health Supplements: What Actually Helps",
      ogDescription:
        "NAC, liposomal silymarin, ALA, curcumin and a named probiotic — how to judge a liver supplement label in India.",
      headline: "Liver Health Supplements: What Actually Helps (India Guide)",
      breadcrumbName: "Liver Health Supplements",
      datePublished: PUBLISHED,
      dateModified: PUBLISHED,
      ogImage: `https://truwe.in${heroAsset}`,
      faqs: FAQS,
    }),
});

function P({ children }: { children: React.ReactNode }) {
  return (
    <p className="mt-4 text-base leading-relaxed text-muted-foreground md:text-[17px]">
      {children}
    </p>
  );
}

function H2({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="mt-12 font-display text-2xl font-extrabold tracking-tight text-brand-deep md:text-3xl">
      {children}
    </h2>
  );
}

function Bullets({ items }: { items: string[] }) {
  return (
    <ul className="mt-4 space-y-2">
      {items.map((s) => (
        <li
          key={s}
          className="flex gap-3 text-base leading-relaxed text-muted-foreground"
        >
          <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-brand" />
          <span className="min-w-0 break-words">{s}</span>
        </li>
      ))}
    </ul>
  );
}

const WHEN_USEFUL = [
  "A routine that regularly includes alcohol, late dinners or fried food",
  "A fatty liver note or raised enzymes on a routine blood test",
  "Long desk hours, high stress and very little movement through the week",
  "Persistent bloating or heaviness after meals, which points at the gut side too",
  "A diet low in vegetables, fibre and antioxidant-rich foods for months at a stretch",
];

const ACTIVES: Array<{ name: string; why: string }> = [
  {
    name: "NAC (N-Acetyl Cysteine)",
    why: "The precursor the body uses to make glutathione, its main internal antioxidant. Well studied, well tolerated and easy to verify on a label because it is listed in plain milligrams.",
  },
  {
    name: "Silymarin (milk thistle), liposomal",
    why: "The most researched botanical for liver cell support. Plain silymarin absorbs poorly, so the delivery form matters as much as the amount.",
  },
  {
    name: "Alpha-lipoic acid",
    why: "An antioxidant that works in both water and fat environments and supports the recycling of other antioxidants.",
  },
  {
    name: "Curcumin, liposomal",
    why: "Ordinary turmeric powder barely reaches the bloodstream. A liposomal form exists specifically to solve that absorption problem.",
  },
  {
    name: "A named multi-strain probiotic",
    why: "The gut and liver share a blood supply. Strains should be named and enteric-protected, with the CFU count stated rather than implied.",
  },
];

const LABEL_CHECKS = [
  "Every active listed with its own amount — not hidden behind a proprietary blend",
  "The delivery form named where it matters (liposomal silymarin, not just milk thistle)",
  "A probiotic listed by strain name and CFU count, not as generic probiotic blend",
  "Manufacturing details: GMP certification and third-party batch testing",
  "A realistic daily dose — one serving a day you will actually keep up for months",
];

const RED_FLAGS = [
  "Three-day or seven-day cleanse kits — the liver does not work to a countdown",
  "Claims to cure, reverse or treat a liver condition, which no supplement is allowed to make",
  "Long ingredient lists at token amounts, added for the label rather than the effect",
  "No mention of absorption for actives that are famously poorly absorbed",
  "No testing or manufacturing information anywhere on the pack or the website",
];

function Article() {
  return (
    <article className="bg-background pb-24 pt-28 md:pt-36">
      <div className="mx-auto max-w-3xl px-4 md:px-8">
        <Reveal>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-brand">
            Clinical Library · Liver &amp; Gut
          </p>
          <h1 className="mt-3 font-display text-4xl font-extrabold leading-tight tracking-tight text-brand-deep md:text-5xl">
            Liver Health Supplements: What Actually Helps
          </h1>
          <BlogByline date={PUBLISHED} readingTime="10 min read" />
          <div className="mt-4">
            <ReadWithLlm title={TITLE} url={URL} kind="article" />
          </div>
          <p className="mt-4 text-base text-muted-foreground md:text-lg">
            A plain guide to liver health supplements in India — the actives with real
            evidence behind them, how to read a label properly, and where everyday
            nutrition does more than any capsule.
          </p>
          <figure className="mt-8 overflow-hidden rounded-3xl border border-border">
            <img
              src={heroAsset}
              alt="A plain supplement bottle beside milk thistle and turmeric root on a matte white surface"
              width={1200}
              height={672}
              loading="eager"
              className="h-auto w-full"
            />
          </figure>
        </Reveal>

        <Reveal delay={0.05}>
          <aside className="mt-8 rounded-2xl border border-brand/20 bg-brand/[0.04] p-5 md:p-6">
            <p className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-brand">
              <Sparkles className="h-3.5 w-3.5" />
              The short answer
            </p>
            <p className="mt-2 text-base leading-relaxed text-brand-deep">
              A liver supplement is worth taking when it supplies the specific nutrition
              your liver's own pathways use — NAC, absorbable silymarin, alpha-lipoic acid,
              absorbable curcumin and a named probiotic — at amounts printed on the label.
              It is not worth taking as a substitute for cutting back on alcohol, sugar and
              fried food, which remains the single biggest lever on liver wellbeing.
            </p>
          </aside>
        </Reveal>

        <Reveal delay={0.05}>
          <H2>What your liver is already doing</H2>
          <P>
            Your liver filters, converts and neutralises around the clock — alcohol,
            medication, hormones and the ordinary by-products of metabolism all pass through
            it. It is not a filter that clogs and needs rinsing; it is a working organ that
            performs better or worse depending on the load you place on it and the nutrition
            you give it.
          </P>
          <P>
            That reframes the whole question. The useful question is not "how do I flush my
            liver", it is "what is my liver short of, and what am I asking it to process
            every day". Answer that honestly and the role of a supplement becomes much
            clearer — and much smaller than most packaging suggests.
          </P>
        </Reveal>

        <Reveal delay={0.05}>
          <H2>When a liver supplement is genuinely useful</H2>
          <P>
            Not everyone needs one. It earns its place when one or more of these describe
            your last few months:
          </P>
          <Bullets items={WHEN_USEFUL} />
          <P>
            If you recognise several of these, start with the habit side — our{" "}
            <Link
              to="/blog/fatty-liver-diet-plan-india"
              className="font-semibold text-brand underline underline-offset-4"
            >
              Indian fatty liver diet plan
            </Link>{" "}
            and{" "}
            <Link
              to="/blog/sluggish-liver-signs-and-fix"
              className="font-semibold text-brand underline underline-offset-4"
            >
              sluggish liver guide
            </Link>{" "}
            cover it in detail — then decide whether a supplement adds anything on top.
          </P>
        </Reveal>

        <Reveal delay={0.05}>
          <H2>The five actives worth looking for</H2>
          <div className="mt-6 space-y-4">
            {ACTIVES.map((a) => (
              <div key={a.name} className="rounded-2xl border border-border bg-card p-5">
                <h3 className="font-display text-base font-bold text-brand-deep md:text-lg">
                  {a.name}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{a.why}</p>
              </div>
            ))}
          </div>
          <P>
            Two of these — silymarin and curcumin — are absorbed so poorly in their plain
            form that the delivery form is not a detail, it is the whole point. Our guide to{" "}
            <Link
              to="/blog/curcumin-bioavailability"
              className="font-semibold text-brand underline underline-offset-4"
            >
              curcumin bioavailability
            </Link>{" "}
            explains why the same milligram number can mean very different things.
          </P>
        </Reveal>

        <Reveal delay={0.05}>
          <H2>The gut half of the story</H2>
          <P>
            Roughly 70% of the blood arriving at your liver comes straight from the
            intestine. When the gut barrier is irritated, the liver deals with more of what
            passes through it. That is why a liver formula that ignores the microbiome is
            solving half a problem — and why a named, enteric-protected probiotic belongs on
            the label rather than in the marketing copy.
          </P>
          <P>
            The{" "}
            <Link
              to="/blog/gut-liver-axis"
              className="font-semibold text-brand underline underline-offset-4"
            >
              gut-liver axis explainer
            </Link>{" "}
            walks through the mechanism, and our{" "}
            <Link
              to="/blog/best-probiotic-for-gut-health-india"
              className="font-semibold text-brand underline underline-offset-4"
            >
              probiotic buying guide
            </Link>{" "}
            covers how to judge strains and CFU counts.
          </P>
        </Reveal>

        <Reveal delay={0.05}>
          <H2>How to read the label in ninety seconds</H2>
          <Bullets items={LABEL_CHECKS} />
          <H2>Five things that should put the bottle back</H2>
          <ul className="mt-4 space-y-2">
            {RED_FLAGS.map((s) => (
              <li
                key={s}
                className="flex gap-3 text-base leading-relaxed text-muted-foreground"
              >
                <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-destructive" />
                <span className="min-w-0 break-words">{s}</span>
              </li>
            ))}
          </ul>
        </Reveal>

        <Reveal delay={0.05}>
          <H2>Nutrition first, always</H2>
          <P>
            Before any capsule: fewer fried items and refined carbohydrates, less alcohol,
            more vegetables and dals, real fibre, coffee if you drink it, and a walk most
            days. These are the levers with the strongest evidence behind them, and they
            cost nothing. A supplement layered on top of good nutrition is support; a
            supplement layered on top of nothing is a placebo with a price tag.
          </P>
          <P>
            Consistency beats intensity. Eight weeks of an ordinary, sustainable routine
            does more for liver wellbeing than a dramatic fortnight followed by a return to
            the old pattern.
          </P>
        </Reveal>

        <Reveal delay={0.05}>
          <H2>Where Liver Biome Plus fits</H2>
          <P>
            If you hold our own formula up against the checklist above, that is exactly what
            it was built from:{" "}
            <Link
              to="/products/$handle"
              params={{ handle: "liver-biome-plus" }}
              className="font-semibold text-brand underline underline-offset-4"
            >
              Liver Biome Plus
            </Link>{" "}
            carries NAC 600 mg, liposomal milk thistle at 350 mg silymarin equivalent,
            liposomal curcumin 100 mg, liposomal alpha-lipoic acid 100 mg and a 10 billion
            CFU multi-strain probiotic, every amount printed on the pack. One serving a day,
            with a meal.
          </P>
          <div className="mt-6">
            <Link
              to="/products/$handle"
              params={{ handle: "liver-biome-plus" }}
              className="inline-flex items-center gap-2 rounded-full bg-brand px-5 py-3 text-sm font-semibold text-white"
            >
              See the full label
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <P>
            If you are still comparing brands, our{" "}
            <Link
              to="/blog/best-liver-detox-supplement-india-guide"
              className="font-semibold text-brand underline underline-offset-4"
            >
              liver detox supplement buyer's guide
            </Link>{" "}
            goes deeper on how to compare two labels side by side.
          </P>
        </Reveal>

        <Reveal delay={0.05}>
          <section className="mt-14">
            <h2 className="font-display text-2xl font-extrabold text-brand-deep md:text-3xl">
              Frequently asked questions
            </h2>
            <div className="mt-6 space-y-3">
              {FAQS.map((f) => (
                <div key={f.q} className="rounded-2xl border border-border bg-card p-5">
                  <h3 className="font-display text-base font-bold text-brand-deep md:text-lg">
                    {f.q}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                    {f.a}
                  </p>
                </div>
              ))}
            </div>
          </section>
        </Reveal>

        <Reveal delay={0.05}>
          <ClinicalProductPitch cluster="liver-gut" />
          <ClusterLinks cluster="liver-gut" />
          <AuthorityFlow cluster="liver-gut" />
          <RelatedReading articles={relatedArticles(SLUG)} />
        </Reveal>

        <Reveal delay={0.05}>
          <p className="mt-12 text-xs text-muted-foreground">
            Educational only. This article does not diagnose or treat any liver condition.
            Speak with a qualified clinician before starting a supplement, especially if you
            have a diagnosed liver condition, take regular medication, or are pregnant or
            breastfeeding.
          </p>
        </Reveal>
      </div>
    </article>
  );
}
